import { useState } from "react";
import { Copy } from "lucide-react";
import metamaskLogo from "../../assets/images/icons/metamask-logo.png";
import roninLogo from "../../assets/images/icons/ronin-ron-logo.png";
import genericLogo from "../../assets/images/icons/generic-wallet-icon.png";
import { useCurrencies } from "../../hooks/useCurrencies";

export default function WalletCard({ player }) {
  const [copied, setCopied] = useState(false);
  const { data } = useCurrencies();

  // Excluded currency IDs
  const excludedCurrencies = ["cur_nana", "cur_cyber", "cur_berry", "cur_polygon_berry", "cur_teeth"];

  // Calculate total guild memberships
  const totalGuildMemberships = player?.wallets?.memberships
    ? Object.entries(player.wallets.memberships).reduce((total, [key, value]) => {
        if (key.startsWith("guild-member-")) {
          return total + (value.count || 0);
        }
        return total;
      }, 0)
    : 0;

  // Function to detect wallet type from wallet data
  const detectWalletType = (walletData) => {
    if (!walletData || !walletData.wallet) return "other"; // Default case

    switch (walletData.wallet.toLowerCase()) {
      case "metamask":
        return "metamask";
      case "ronin":
        return "ronin";
      default:
        return "other"; // Generic wallet fallback
    }
  };

  // Function to format wallet address (first 5 and last 5 characters)
  const formatWalletAddress = (address) => {
    if (!address) return "";
    return `${address.slice(0, 5)}...${address.slice(-5)}`;
  };

  // Function to copy wallet address to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500); // hide after 1.5s
    });
  };

  const getCurrencyData = (currencyId) => {
    if (!data) return;
    return data.find((c) => c.id === currencyId);
  };

  const nftLandPixels = player?.wallets?.memberships?.["nftLand-pixels"]?.count || 0;
  const pixelPets = player?.pets?.length ?? 0;

  // Filter out excluded currencies and currencies with a balance of 0
  const filteredCoinInventory = player?.wallets?.coinInventory?.filter((token) => !excludedCurrencies.includes(token.currencyId) && token.balance > 0) || [];

  return (
    <div className="bg-[var(--panel-section-bg)] bg-opacity-80 w-full text-[var(--text-color)] rounded-2xl shadow-lg p-6 border border-[var(--border-color)]  transition-transform duration-300 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)] ">
      <h2 className="text-lg font-bold text-center w-full">Wallet Overview</h2>

      {/* Wallets Section - Updated for cryptoWalletAddresses */}
      {player?.wallets?.cryptoWalletAddresses?.length > 0 && (
        <div className="mt-1">
          <h3 className="text-xs text-center font-semibold text-[var(--level-bars)] ">Registered Wallets</h3>
          <ul className="mt-2 space-y-2">
            {copied && (
              <div className="absolute -top-7 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded shadow-lg z-10 transition-opacity duration-300">
                Copied!
              </div>
            )}
            {player.wallets.cryptoWalletAddresses.map((walletData, index) => {
              return (
                <li
                  key={index}
                  className="flex items-center justify-between bg-[var(--panel-sections)]  p-2 rounded-md text-sm border border-[var(--entity-bg-color)] hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)]  hover:scale-105"
                >
                  <div className="flex items-center gap-2">
                    {/* Wallet Icon */}
                    <img
                      src={detectWalletType(walletData) === "metamask" ? metamaskLogo : detectWalletType(walletData) === "ronin" ? roninLogo : genericLogo}
                      alt={detectWalletType(walletData)}
                      className="w-6 h-6 object-contain"
                    />
                    {/* Formatted Address */}
                    <span className="text-[var(--text-color)] ">{formatWalletAddress(walletData.address)}</span>
                  </div>
                  {/* Copy Button */}
                  <button onClick={() => copyToClipboard(walletData.address)} className="text-[var(--icons)] hover:text-[var(--level-bars)] transition-colors">
                    <Copy size={16} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Tokens and NFT Holdings Sections */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        {/* Tokens Section */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--level-bars)] ">Tokens</h3>
          <ul className="mt-2 space-y-2 max-h-[200px] overflow-hidden hover:overflow-y-auto hover:overflow-x-none">
            {filteredCoinInventory.map((token, index) => {
              const currency = getCurrencyData(token.currencyId);

              return (
                <li
                  key={index}
                  className="group flex items-center justify-between bg-[var(--panel-sections)]  p-2 rounded-md text-xs border border-[var(--entity-bg-color)] transition-transform duration-300 hover:scale-105 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--entity-bg-color)]"
                >
                  <div className="flex items-center gap-2">
                    {currency?.image && <img src={`https:${currency.image}`} alt={currency.name} className="w-6 h-6 object-contain" />}
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">{currency?.name || token.currencyId}</span>
                  </div>
                  <span>{token.balance.toLocaleString()}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* NFT Holdings Section */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--level-bars)] ">NFT Holdings</h3>
          <ul className="mt-2 space-y-2 text-xs">
            <li className="flex justify-between bg-[var(--panel-sections)]  p-2 rounded-md border border-[var(--entity-bg-color)] transition-transform duration-300 hover:scale-105 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--entity-bg-color)]">
              <span>Pixel Pets</span>
              <span>{pixelPets.toLocaleString()}</span>
            </li>
            <li className="flex justify-between bg-[var(--panel-sections)]  p-2 rounded-md border border-[var(--entity-bg-color)] transition-transform duration-300 hover:scale-105 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--entity-bg-color)]">
              <span>Pixel Lands</span>
              <span>{nftLandPixels.toLocaleString()}</span>
            </li>
            <li className="flex justify-between bg-[var(--panel-sections)]  p-2 rounded-md border border-[var(--entity-bg-color)] transition-transform duration-300 hover:scale-105 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--entity-bg-color)]">
              <span>Guild Memberships</span>
              <span>{totalGuildMemberships.toLocaleString()}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
